import { useEffect, useMemo } from 'react';
import { AlertTriangle, Check, Link2, Sparkles, FileSearch } from 'lucide-react';
import { useFontAliasStore } from '../../stores/fontAliasStore';
import { useFontStore } from '../../stores/fontStore';
import { useStudioStore } from '../../stores/studioStore';
import {
  collectProjectFontUsage,
  resolveFontForCss,
  type FontResolution,
  type FontResolutionStatus,
} from '../../lib/fontResolution';

const STATUS_META: Record<FontResolutionStatus, { label: string; badge: string; icon: typeof Check }> = {
  resolved: {
    label: 'Resolved',
    badge: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30',
    icon: Check,
  },
  aliased: {
    label: 'Aliased',
    badge: 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30',
    icon: Link2,
  },
  heuristic: {
    label: 'Heuristic',
    badge: 'bg-amber-500/10 text-amber-400 border border-amber-500/30',
    icon: Sparkles,
  },
  missing: {
    label: 'Missing',
    badge: 'bg-red-500/10 text-red-400 border border-red-500/30',
    icon: AlertTriangle,
  },
};

export function ProjectFontsTab({ onGoToMappings }: { onGoToMappings?: () => void }) {
  const ensureLoaded = useFontAliasStore((s) => s.ensureLoaded);
  const aliases = useFontAliasStore((s) => s.aliases);
  const fonts = useFontStore((s) => s.fonts);
  const elements = useStudioStore((s) => s.canvasState.elements);
  const selectElement = useStudioStore((s) => s.selectElement);
  const closeManager = useFontStore((s) => s.closeManager);

  useEffect(() => {
    ensureLoaded().catch(() => {});
  }, [ensureLoaded]);

  const rows = useMemo(() => {
    const usage = collectProjectFontUsage(elements);
    return usage.map((r) => ({
      ...r,
      resolution: resolveFontForCss(r.cssValue, fonts, aliases),
    }));
  }, [elements, fonts, aliases]);

  const counts = useMemo(() => {
    const c: Record<FontResolutionStatus, number> = { resolved: 0, aliased: 0, heuristic: 0, missing: 0 };
    for (const r of rows) c[r.resolution.status]++;
    return c;
  }, [rows]);

  if (rows.length === 0) {
    return (
      <div className="p-6 text-center py-12">
        <FileSearch size={40} className="mx-auto text-white/20 mb-3" />
        <p className="text-sm text-white/50 mb-1">No text elements in this project yet</p>
        <p className="text-xs text-white/30">
          Add text or import an SVG with text to see which platform fonts are in use.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <StatusPill status="resolved" count={counts.resolved} />
        <StatusPill status="aliased" count={counts.aliased} />
        <StatusPill status="heuristic" count={counts.heuristic} />
        <StatusPill status="missing" count={counts.missing} />
      </div>

      <div className="space-y-2">
        {rows.map((row) => (
          <ProjectFontRow
            key={row.cssValue}
            cssValue={row.cssValue}
            elementIds={row.elementIds}
            resolution={row.resolution}
            onSelectElement={(id) => {
              selectElement(id);
              closeManager();
            }}
            onOpenMappings={onGoToMappings}
          />
        ))}
      </div>
    </div>
  );
}

function StatusPill({ status, count }: { status: FontResolutionStatus; count: number }) {
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-medium ${meta.badge} ${
        count === 0 ? 'opacity-40' : ''
      }`}
    >
      <Icon size={11} />
      {meta.label}
      <span className="text-[10px] opacity-80">{count}</span>
    </span>
  );
}

function ProjectFontRow({
  cssValue,
  elementIds,
  resolution,
  onSelectElement,
  onOpenMappings,
}: {
  cssValue: string;
  elementIds: string[];
  resolution: FontResolution;
  onSelectElement: (id: string) => void;
  onOpenMappings?: () => void;
}) {
  const meta = STATUS_META[resolution.status];
  const Icon = meta.icon;

  return (
    <div className="border border-white/10 rounded-lg p-3">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${meta.badge}`}
            >
              <Icon size={10} />
              {meta.label}
            </span>
            {resolution.status === 'heuristic' && resolution.heuristicTier && (
              <span className="text-[10px] text-white/40 uppercase tracking-wider">
                {resolution.heuristicTier}
              </span>
            )}
            <span className="text-[11px] text-white/40">
              {elementIds.length} element{elementIds.length === 1 ? '' : 's'}
            </span>
          </div>

          <div
            className="text-sm text-white/90 truncate font-mono"
            title={cssValue}
            style={
              resolution.resolvedFamily
                ? { fontFamily: `"${resolution.resolvedFamily}", sans-serif` }
                : undefined
            }
          >
            {cssValue}
          </div>

          {resolution.resolvedFamily && resolution.resolvedFamily !== cssValue && (
            <div className="text-[11px] text-white/50 mt-1">
              resolves to <span className="text-white/80">{resolution.resolvedFamily}</span>
              {resolution.resolvedWeight !== null && (
                <> &middot; {resolution.resolvedWeight}</>
              )}
              {resolution.resolvedStyle === 'italic' && <> italic</>}
            </div>
          )}

          {resolution.status === 'missing' && (
            <div className="text-[11px] text-red-300/80 mt-1">
              No uploaded family and no alias matches. Text will render in a browser fallback font.
            </div>
          )}
        </div>

        <div className="flex flex-col items-end gap-1 shrink-0">
          {resolution.status === 'missing' || resolution.status === 'heuristic' ? (
            <button
              onClick={onOpenMappings}
              className="px-2 py-1 text-[11px] rounded bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-colors font-medium"
              title="Create or edit the mapping"
            >
              Fix mapping
            </button>
          ) : null}
          {elementIds.length > 0 && (
            <button
              onClick={() => onSelectElement(elementIds[0])}
              className="px-2 py-1 text-[11px] rounded border border-white/10 text-white/60 hover:text-white hover:border-white/20 transition-colors"
              title="Select the first element using this font"
            >
              Reveal on canvas
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
